import { NativeLocalVideoPreview } from '@src/components/common/NativeLocalVideoPreview';
import { CompositionCanvasActionsLayer } from '@src/components/content-components/CompositionCanvasActionsLayer';
import { usePlayerVolume } from '@src/hooks/usePlayerVolume';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { isOriginalLang } from '@src/utility/captionLanguage';
import { forIPad } from '@src/utility/styled';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';

import {
  useEditor,
  useLayoutStore,
  useTranscription,
  useUserIdentity,
  useVideoSource,
} from '../../stores-v2';
import CirclePlayFilledIcon from '../../svgs/CirclePlayFilledIcon';
import CloseIcon from '../../svgs/CloseIcon';
import ForwardToEndIcon from '../../svgs/ForwardToEndIcon';
import { FullScreenIconAlt } from '../../svgs/FullScreenIcon';
import PauseBarsIcon from '../../svgs/PauseBarsIcon';
import PlayForwardIcon from '../../svgs/PlayForwardIcon';
import VerticalLine from '../../svgs/VerticalLine';
import { inDebugMode } from '../../utility/debug';
import { timeFormat } from '../../utility/timeFormat';
import { useFlagsCombination } from '../../utility/useFlagsCombination';
import useLoadBrandKitFonts from '../BrandKit/utils/useLoadBrandKitFonts';
import CaptionsFloatingToolbar from '../common/floatingToolbar/CaptionsFloatingToolbar';
import { FloatingToolbarHostProvider } from '../common/floatingToolbar/FloatingToolbarHost';
import ImageFloatingToolbar from '../common/floatingToolbar/ImageFloatingToolbar';
import TextFloatingToolbar from '../common/floatingToolbar/TextFloatingToolbar';
import VideoFloatingToolbar from '../common/floatingToolbar/VideoFloatingToolbar';
import {
  PlayBackward,
  PlayButton,
  PlayForward,
  Time,
} from '../common/PlaybackControls';
import { PlayerSettingsSlot } from '../common/playerSettingsSlot';
import { PlayerVolumeControl } from '../common/PlayerVolumeControl';
import { LoadingState } from '../common/Spinner';
import { UndoRedoButtons } from '../common/UndoRedoButtons';
import DebugModal from '../debug/DebugModal';
import {
  dismissFromWorkspace,
  isStageWorkspacePress,
  STAGE_WORKSPACE_ATTR,
} from '../sidepanel/rightPanelDismiss';
import AspectRatiosDropdown from './AspectRatiosDropdown';
import { useCreatorStudioToolbarHost } from './creatorStudioToolbarHost';
import FitDropdown from './FitDropdown';
import { useFullscreenTransition } from './hooks/useFullscreenTransition';
import { LetterboxPressTargets } from './LetterboxPressTargets';
import ReframeVideoIcon from './ReframeVideoIcon';
import {
  BottomItems,
  BottomItemsLeft,
  BottomItemsMid,
  BottomItemsRight,
} from './StageBottomItems';
import { StageDebugMenu } from './StageDebugMenu';
import { useRendererRect } from './useRendererRect';

import type { AspectRatio } from '../../types.ts/video';

/**
 * Marks the stage's own layout boxes as the empty workspace around the
 * canvas (PLAT-12096): a press directly on one of them, not on a control
 * inside it, dismisses — see `dismissOnWorkspacePress`.
 */
const WORKSPACE = { [STAGE_WORKSPACE_ATTR]: '' };

interface Props {
  storyTellerName: null | string;
  nativeLocalPreviewUrl?: string | null;
  /**
   * PLAT-11224 (audio-first): the pending-preview veil already reports the
   * canvas state, and the "Loading..." pill would blink on top of it on every
   * mid-pending render pass — reads as veil flicker. Hide it while veiled.
   */
  hideLoadingIndicator?: boolean;
}

export const Stage: React.FC<Props> = observer(props => {
  const { hideLoadingIndicator, nativeLocalPreviewUrl } = props;
  const videoCreator = useVideoCreatorStore();
  const toolbarHost = useCreatorStudioToolbarHost();
  const layoutStore = useLayoutStore();
  const editorStore = useEditor();
  const videoSourceStore = useVideoSource();
  const transcriptionStore = useTranscription();
  const userIdentity = useUserIdentity();
  const {
    creatorStudioTimelineEnableReframingDropdown,
    enableCompositionCanvasQuickEdit,
    contentStudioEnableTranslatedUndoRedo,
  } = useFlagsCombination(userIdentity.currentRole);
  // The transcript panel's language selection lives in the URL (`?lang=`,
  // owned by LeftSidePanel), so the same read tells the player controls
  // whether a translation is on screen — no prop drilling through MainView.
  const [searchParams] = useSearchParams();
  const showUndoRedo =
    contentStudioEnableTranslatedUndoRedo ||
    isOriginalLang(searchParams.get('lang'));
  const stageAreaRef = React.useRef<HTMLDivElement>(null);
  // PLAT-10211 — canvas container for the composition action layer's element
  // geometry. Bound to the same element the renderer initializes into.
  const canvasContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [canvasContainerElement, setCanvasContainerElement] =
    React.useState<HTMLDivElement | null>(null);

  const [isDebugModalOpen, setDebugModalOpen] = useState(false);
  const [debugContent, setDebugContent] = useState<any>(null);
  const framerate = videoCreator.timelineStore.framerate;

  useLoadBrandKitFonts(videoCreator);

  // Master volume/mute for stage playback — same shared control as the
  // Content Studio clip preview. `isPlaying` doubles as the reconcile token
  // so a non-default factor is re-pushed after play-start source refreshes.
  const volumeControls = usePlayerVolume({
    enabled: !!videoCreator.renderer?.ready,
    getRenderer: () => videoCreator.renderer ?? null,
    getActiveElementIds: () => videoCreator.activeElementIds.slice(),
    isPlaying: videoCreator.isPlaying,
    reconcileToken: videoCreator.isPlaying,
  });

  const { makeFullScreen, exitFullScreen } = useFullscreenTransition();

  React.useEffect(() => {
    return () => {
      videoCreator.disposeRenderer();
    };
  }, []);

  const openDebugModal = () => {
    const debugContent = videoCreator.getDebugContent();
    setDebugContent(debugContent);
    setDebugModalOpen(true);
  };

  const closeDebugModal = () => {
    setDebugContent(null);
    setDebugModalOpen(false);
  };

  const currTime =
    videoCreator.isPlayheadDragging && videoCreator.tempDragTime != null
      ? videoCreator.tempDragTime
      : videoCreator.time;
  const localSourceTime =
    (videoCreator.currentVideo?.clipJson?.startTime ?? 0) + currTime;
  const localPreviewRect = useRendererRect(
    canvasContainerElement,
    videoCreator.currentVideo?.aspectRatio,
  );
  const localPreviewBounds = {
    left: localPreviewRect.containerLeft + localPreviewRect.overlayLeft,
    top: localPreviewRect.containerTop + localPreviewRect.overlayTop,
    width: localPreviewRect.overlayWidth,
    height: localPreviewRect.overlayHeight,
  };
  // The canvas container element is tracked in state only for the native
  // local-preview bounds (useRendererRect). Read it after commit: setting
  // state from inside the ref callback re-entered the commit and the renderer
  // never got created (no iframe, Stage stuck on LOADING).
  React.useEffect(() => {
    setCanvasContainerElement(canvasContainerRef.current);
  });

  // PLAT-12096 — a press on the empty stage around the canvas deselects and
  // closes whichever right-hand panel is open. Only the marked layout boxes
  // count: a press on a toolbar control, the floating text toolbar or the
  // canvas pill (portaled, but React still bubbles it here) does nothing.
  // The letterbox inside the renderer iframe is covered by
  // LetterboxPressTargets, which carry the same marker. Presses on the picture
  // are the renderer's; its deselect closes the panel too
  // (VideoCreatorStore.onActiveElementsChange).
  const dismissOnWorkspacePress = (e: React.MouseEvent) => {
    if (!isStageWorkspacePress(e.target)) return;
    dismissFromWorkspace(
      videoCreator,
      layoutStore.isSidebarExpanded
        ? () => videoCreator.collapseSidebar()
        : null,
    );
  };

  return (
    <Main
      {...WORKSPACE}
      onMouseDown={dismissOnWorkspacePress}
      data-testid="creator-studio-stage"
      data-shortcut-scope="timeline-stage"
      isFullScreen={videoCreator.isPlayerFullScreen}
      ref={stageAreaRef}
      tabIndex={-1}
      onClick={e => {
        if (videoCreator.isPlayerFullScreen && e.target === e.currentTarget) {
          exitFullScreen();
        }
      }}
    >
      <MainArea
        {...WORKSPACE}
        isFullScreen={videoCreator.isPlayerFullScreen}
        onClick={e => {
          if (videoCreator.isPlayerFullScreen && e.target === e.currentTarget) {
            exitFullScreen();
          }
        }}
      >
        <MediaContainer
          {...WORKSPACE}
          isFullScreen={videoCreator.isPlayerFullScreen}
        >
          {videoCreator.isPlayerFullScreen && (
            <ExitFullScreenButton onClick={exitFullScreen}>
              <CloseIcon />
            </ExitFullScreenButton>
          )}
          <PlayerContainer
            {...WORKSPACE}
            isFullScreen={videoCreator.isPlayerFullScreen}
          >
            {!videoCreator.isPlayerFullScreen && (
              <TopItems
                {...WORKSPACE}
                aspectRatio={videoCreator.currentVideo?.aspectRatio}
                timelineHeight={parseFloat(layoutStore.timelineHeight)}
              >
                <TopContent {...WORKSPACE}>
                  <TopActions {...WORKSPACE}>
                    <TopLeftAction>
                      <AspectRatiosDropdown />
                      {creatorStudioTimelineEnableReframingDropdown && (
                        <>
                          <FitDropdown />
                          <ReframeVideoIcon />
                        </>
                      )}
                    </TopLeftAction>
                    {/* PLAT-11877 — Captions / Voice / Music, put here by
                        the left panel (it owns the dub composition): the
                        row above the picture, as the clip preview has it. */}
                    <TopRightAction>
                      <PlayerSettingsSlot />
                      {/* PLAT-12095 — debug details live behind a kebab at
                          the end of the row, not floating over the pickers. */}
                      {inDebugMode() && (
                        <StageDebugMenu
                          migrationLog={
                            editorStore.transcriptSegmentArchitectureMigrationLog
                          }
                          onOpenDebugData={openDebugModal}
                        />
                      )}
                    </TopRightAction>
                  </TopActions>
                </TopContent>
              </TopItems>
            )}
            <VideoArea
              {...WORKSPACE}
              isFullScreen={videoCreator.isPlayerFullScreen}
            >
              {/* PLAT-11778: the wait the user feels after picking another
                  clip is the canvas (source push, then assets), so the
                  indicator lives in the video area — the header title has
                  already flipped and the dropdown that started it is gone.
                  It sits over the stale frame rather than replacing it (the
                  renderer stays mounted) and fades in after the design
                  system's ~300ms floor so a quick asset load never flashes
                  it. Thumbnails are a timeline wait, not a canvas one. */}
              {(videoCreator.isLoading || videoCreator.isLoadingAssets) &&
                !hideLoadingIndicator && (
                  <CanvasLoading role="status" aria-live="polite">
                    <LoadingState message="Loading…" />
                  </CanvasLoading>
                )}
              <LoadingIndicator>
                {videoCreator.isSaving && <LoadingText>Saving...</LoadingText>}
              </LoadingIndicator>
              <VideoPlayer
                {...WORKSPACE}
                isLayoutDragging={layoutStore.isDragging}
                isFullScreen={videoCreator.isPlayerFullScreen}
                aspectRatio={videoCreator.currentVideo?.aspectRatio}
                ref={element => {
                  canvasContainerRef.current = element;
                  if (element && element !== videoCreator.renderer?.element) {
                    videoCreator.initializeVideoPlayer(element);
                  }
                }}
              />
              {nativeLocalPreviewUrl && localPreviewBounds.width > 0 && (
                <NativeLocalVideoPreview
                  src={nativeLocalPreviewUrl}
                  sourceTime={localSourceTime}
                  isPlaying={videoCreator.isPlaying}
                  bounds={localPreviewBounds}
                />
              )}
              <LetterboxPressTargets
                canvas={localPreviewBounds}
                containerWidth={localPreviewRect.parentWidth}
                containerHeight={localPreviewRect.parentHeight}
                disabled={layoutStore.isDragging}
              />
              {/* PLAT-10211 — composition action menu (pencil/trash pill +
                  quick-edit popover) over the canvas. Driven by
                  VideoCreatorStore.activeElementIds, so a canvas click OR a
                  timeline selection (PLAT-10122) opens it. Portals to
                  document.body. */}
              {enableCompositionCanvasQuickEdit && (
                <CompositionCanvasActionsLayer
                  rendererContainerRef={canvasContainerRef}
                  adapter={videoCreator}
                  editorStore={editorStore}
                  videoSourceStore={videoSourceStore}
                  // PLAT-10935 DS1 — the Creator Studio editor store carries no
                  // transcript, so the AI text control gets the story transcript
                  // from `TranscriptionStore` instead.
                  transcriptElements={transcriptionStore.transcriptionElements}
                  storyId={videoCreator.storyId}
                  surface="creator-studio-canvas"
                  // PLAT-12102 DS5 — the stage always mounts the bars.
                  barsMounted
                  // PLAT-10191 — flush the post-delete editor state to Dato so a
                  // canvas delete persists like an add (the command's own
                  // autosave snapshots the pre-delete state). Same background
                  // autosave the app already uses.
                  // Args: saveStoryAndVideo(asFinal=false, withRenderer=true,
                  // resetTimeline=false, autoSave=true) — re-export the live
                  // post-delete editor state and background-autosave it.
                  onPersistAfterMutation={() =>
                    void videoCreator.saveStoryAndVideo(
                      false,
                      true,
                      false,
                      true,
                    )
                  }
                />
              )}
            </VideoArea>
            {!videoCreator.isPlayerFullScreen && (
              <BottomItems canvasWidth={localPreviewRect.overlayWidth}>
                <BottomItemsLeft>
                  {/* Hidden while the transcript shows a translation: the undo
                      stack doesn't cover translated edits, so the buttons only
                      look like they work (flag defaults on). */}
                  {showUndoRedo && (
                    <UndoRedoButtons
                      canUndo={videoCreator.canUndo}
                      canRedo={videoCreator.canRedo}
                      getUndoLabel={() => videoCreator.getUndoLabel() || ''}
                      getRedoLabel={() => videoCreator.getRedoLabel() || ''}
                      onUndo={() => videoCreator.undo()}
                      onRedo={() => videoCreator.redo()}
                    />
                  )}
                  <PlayerVolumeControl
                    {...volumeControls}
                    disabled={!videoCreator.renderer?.ready}
                  />
                </BottomItemsLeft>
                <BottomItemsMid>
                  <PlayBackward
                    disabled={currTime <= 0}
                    onClick={() => {
                      if (!videoCreator.renderer?.ready) return;
                      const wasPlaying = videoCreator.isPlaying;
                      videoCreator.renderer?.setTime(0);
                      if (wasPlaying) {
                        videoCreator.renderer?.play();
                      }
                    }}
                  >
                    <ForwardToEndIcon width="18" height="14" />
                  </PlayBackward>

                  <PlayBackward
                    disabled={currTime <= 0}
                    onClick={() => {
                      if (!videoCreator.renderer?.ready) return;
                      const wasPlaying = videoCreator.isPlaying;
                      videoCreator.renderer?.setTime(videoCreator.time - 15);
                      if (wasPlaying) {
                        videoCreator.renderer?.play();
                      }
                    }}
                  >
                    <PlayForwardIcon width="18" height="14" />
                  </PlayBackward>
                  <PlayButton
                    background={false}
                    onClick={() => {
                      if (!videoCreator.isPlaying) {
                        videoCreator.refreshSourceAndPlay();
                      } else {
                        videoCreator.renderer?.pause();
                      }
                    }}
                  >
                    {!videoCreator.isPlaying ? (
                      <CirclePlayFilledIcon
                        width="32"
                        height="32"
                        fillColor="var(--foreground)"
                      />
                    ) : (
                      <PauseBarsIcon
                        width="32"
                        height="32"
                        fillColor="var(--foreground)"
                      />
                    )}
                  </PlayButton>
                  <PlayForward
                    disabled={currTime >= videoCreator.duration}
                    onClick={() => {
                      if (!videoCreator.renderer?.ready) return;
                      const wasPlaying = videoCreator.isPlaying;
                      videoCreator.renderer?.setTime(videoCreator.time + 15);
                      if (wasPlaying) {
                        videoCreator.renderer?.play();
                      }
                    }}
                  >
                    <PlayForwardIcon width="18" height="14" />
                  </PlayForward>
                  <PlayForward
                    disabled={currTime >= videoCreator.duration}
                    onClick={() => {
                      if (!videoCreator.renderer?.ready) return;
                      videoCreator.renderer?.setTime(videoCreator.duration);
                    }}
                  >
                    <ForwardToEndIcon width="18" height="14" />
                  </PlayForward>
                </BottomItemsMid>
                <BottomItemsRight>
                  <Time curHasHours={currTime > 3600}>
                    <span className="current">
                      {timeFormat(currTime, framerate)}
                    </span>
                    <VerticalLine />
                    <span className="end">
                      {timeFormat(
                        videoCreator.duration,
                        framerate,
                        videoCreator.duration > 3600,
                      )}
                    </span>
                  </Time>
                  <FullscreenAction role="button" onClick={makeFullScreen}>
                    <FullScreenIconAlt />
                  </FullscreenAction>
                </BottomItemsRight>
              </BottomItems>
            )}
          </PlayerContainer>
        </MediaContainer>
      </MainArea>
      {/* Canvas chrome, so it lives in the stage's stacking context rather than
          a body portal: that keeps the AR / Fit dropdowns above it, and keeps
          it visible in fullscreen (where `Main` becomes an opaque fixed layer
          that would bury a sibling of the stage). */}
      {/* PLAT-12101 — the bars read the stage through this host, not the
          Creator Studio store directly. */}
      <FloatingToolbarHostProvider host={toolbarHost}>
        <TextFloatingToolbar />
        {/* Same stacking context, for the same reasons (PLAT-10405). Each
            toolbar renders only for its own element type, so at most one of
            the four shows (captions: PLAT-12100). */}
        <ImageFloatingToolbar />
        <VideoFloatingToolbar />
        <CaptionsFloatingToolbar />
      </FloatingToolbarHostProvider>
      {isDebugModalOpen && (
        <DebugModal
          onClose={closeDebugModal}
          content={debugContent}
          title="Debug Data"
        />
      )}
    </Main>
  );
});

const Main = styled.div<{ isFullScreen: boolean }>`
  position: relative;
  /* Above both side panels, so the wide TextFloatingToolbar can overhang a
     narrow canvas without the right panel clipping it. */
  z-index: 1;
  display: flex;
  flex: 1;
  flex-direction: column;
  &:focus,
  &:focus-visible {
    outline: none;
  }

  ${forIPad(`
    min-width: 438px;
  `)}

  ${props =>
    props.isFullScreen &&
    css`
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 100000;
      background: var(--background);
      align-items: center;
      justify-content: center;
    `}
`;

const MainArea = styled.div<{ isFullScreen: boolean }>`
  position: relative;

  display: flex;
  ${props =>
    props.isFullScreen &&
    css`
      height: 80%;
      width: 100%;
      margin: auto;

      @media (max-width: 2100px) {
        gap: 20px;
      }

      @media (max-width: 1600px) {
        gap: 15px;
      }

      @media (max-width: 1400px) {
        gap: 10px;
      }

      @media (max-width: 1200px) {
        gap: 5px;
      }
    `}

  ${props =>
    !props.isFullScreen &&
    css`
      flex: 1;
    `}
`;

const TopItems = styled.div<{
  aspectRatio?: AspectRatio;
  timelineHeight: number;
}>`
  display: flex;
  justify-content: space-between;
  padding: 16px;
`;

const TopContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`;

const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  .line {
    opacity: 0.3;
  }
`;

const canvasLoadingFadeIn = keyframes`
  to {
    opacity: 1;
  }
`;

// Centred in VideoArea (position: relative). No full-area scrim: the frame
// behind stays as context, and pointer-events pass through so an in-flight
// canvas interaction is never swallowed.
const CanvasLoading = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  opacity: 0;
  animation: ${canvasLoadingFadeIn} 0s linear 300ms forwards;

  > * {
    padding: 12px 20px;
    border-radius: var(--radius);
    background: var(--overlay-dark);
  }
`;

const TopActions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const TopLeftAction = styled.div`
  margin-right: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TopRightAction = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LoadingText = styled.div`
  position: absolute;
  z-index: 10001;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  padding: 4px 8px;
  align-items: center;
  border-radius: 4px;
  background: var(--accent);
  color: var(--background);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  text-transform: uppercase;
`;

const MediaContainer = styled.div<{ isFullScreen: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 50px;
  justify-content: center;
  align-items: center;

  ${props =>
    props.isFullScreen &&
    css`
      width: 80%;
      height: 100%;
      margin: auto;
    `}

  ${props =>
    !props.isFullScreen &&
    css`
      width: 100%;
      height: 100%;
      margin: 0 auto;
    `}
`;

/* Top toolbar and transport row take their own height; the canvas gets what
   is left. Sizing the rows by percentage (the old grid) starved the transport
   row whenever the timeline grew or the row needed two lines. */
const PlayerContainer = styled.div<{ isFullScreen: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  margin-top: 0;
`;

const VideoArea = styled.div<{ isFullScreen: boolean }>`
  position: relative;
  display: flex;
  flex: 1 1 0;
  min-height: 0;
  width: 100%;

  ${props =>
    props.isFullScreen &&
    css`
      height: 100%;
      margin: auto;
    `}
`;

const VideoPlayer = styled.div.attrs({
  id: 'video-player',
})<{
  aspectRatio?: AspectRatio.AR_16_9 | AspectRatio.AR_1_1 | AspectRatio.AR_9_16;
  isFullScreen: boolean;
  isLayoutDragging: boolean;
}>`
  padding-right: 0;
  border-radius: 20px;
  position: relative;
  display: flex;
  flex: 1;
  width: 100%;
  height: 100%;

  ${props =>
    props.isLayoutDragging &&
    css`
      pointer-events: none; /* otherwise video player eats up drag events */
    `}
`;

const ExitFullScreenButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(calc(100% + 24px), 0);
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: var(--spacing-sm);
  border-radius: 6px;
  transition: all 0.15s ease;
  z-index: 10;

  &:hover {
    color: var(--foreground);
    background-color: var(--surface-elevated);
  }
`;

const FullscreenAction = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 150ms ease-out;

  &:hover {
    svg path {
      fill: var(--foreground);
    }
  }
`;
